import {Settings} from 'luxon';

import defaultCultureData from '@/cultures/en-US.json';

export type CultureMaskType = 'short' | 'medium' | 'long' | 'full';

type CultureMaskCollection = Record<CultureMaskType, string> & Record<string, string>;

export interface CultureDefinition {
  cultureCode: string,
  name: string,
  language: {
    iso2: string | false,
    iso3: string,
    name: string,
  },
  country: {
    iso2: string | false,
    iso3: string,
    name: string,
  },
  currency: {
    code: string,
    decimalPoint: string,
    name: string,
    symbol: string,
  },
  numbers: {
    decimalPoint: string,
    grouping: string,
    minusSign: string,
  },
  masks: {
    date: CultureMaskCollection,
    dateTime: CultureMaskCollection,
    time: CultureMaskCollection,
  },
  [key: string]: unknown,
}

type CultureModule = {default: CultureDefinition};
type CultureLoader = () => Promise<CultureModule>;

const DEFAULT_CULTURE_CODE = 'en-US';
const cultureModules = import.meta.glob<CultureModule>([
  '../cultures/*.json',
  '!../cultures/en-US.json',
]);
const cultureLoaders = new Map<string, CultureLoader>(
  Object.entries(cultureModules).map(([filePath, loader]) => [
    cultureLookupKey(filePath.split('/').pop()?.replace(/\.json$/, '') ?? ''),
    loader,
  ]),
);
cultureLoaders.set(cultureLookupKey(DEFAULT_CULTURE_CODE), async () => ({
  default: defaultCultureData as CultureDefinition,
}));

let activeCulture = defaultCultureData as CultureDefinition;
Settings.defaultLocale = activeCulture.cultureCode;

export function normalizeCultureCode(cultureCode?: string | null): string {
  const normalized = cultureCode?.trim().replace(/_/g, '-');

  return normalized || DEFAULT_CULTURE_CODE;
}

export async function setCulture(cultureCode?: string | null): Promise<CultureDefinition> {
  const requestedCultureCode = normalizeCultureCode(cultureCode);
  const loader = cultureLoaders.get(cultureLookupKey(requestedCultureCode))
    ?? cultureLoaders.get(cultureLookupKey(DEFAULT_CULTURE_CODE));

  if (!loader) {
    activeCulture = defaultCultureData as CultureDefinition;
  } else {
    try {
      activeCulture = (await loader()).default;
    } catch {
      activeCulture = defaultCultureData as CultureDefinition;
    }
  }

  Settings.defaultLocale = activeCulture.cultureCode;
  if (typeof document !== 'undefined') {
    document.documentElement.lang = activeCulture.cultureCode;
  }

  return activeCulture;
}

function cultureLookupKey(cultureCode: string): string {
  return normalizeCultureCode(cultureCode).toLowerCase();
}

export function getCulture(): CultureDefinition {
  return activeCulture;
}

export function getCultureCode(): string {
  return activeCulture.cultureCode;
}

export function getLanguageCode(): string {
  return activeCulture.language.iso2 || activeCulture.cultureCode.split('-')[0];
}

export function getCountryCode(): string {
  return activeCulture.country.iso2 || activeCulture.cultureCode.split('-')[1] || '';
}

export function getCountry(): string {
  return activeCulture.country.name;
}

export function getDateMask(maskType: string): string {
  return activeCulture.masks.date[maskType] ?? maskType;
}

export function getTimeMask(maskType: string): string {
  return timeMaskToLuxon(activeCulture.masks.time[maskType] ?? maskType);
}

export function getDateTimeMask(dateMaskType: string, timeMaskType?: string): string {
  const dateTimeMask = activeCulture.masks.dateTime[dateMaskType];

  if ((!timeMaskType || dateMaskType === timeMaskType) && dateTimeMask) {
    return timeMaskToLuxon(dateTimeMask);
  }

  return `${getDateMask(dateMaskType)} ${getTimeMask(timeMaskType ?? dateMaskType)}`;
}

export function dateMaskToDatePicker(dateMask: string): string {
  return convertUnquotedTokens(dateMask, (token) => {
    if (token[0] === 'y') {
      return token.length >= 4 ? 'yy' : 'y';
    }

    if (token[0] === 'M') {
      return token.length >= 4 ? 'MM' : token.length === 3 ? 'M' : token.length === 2 ? 'mm' : 'm';
    }

    if (token[0] === 'E') {
      return token.length >= 4 ? 'DD' : 'D';
    }

    return token;
  });
}

export function getDatePickerMask(maskType: string): string {
  const mask = activeCulture.masks.date[maskType];

  return mask ? dateMaskToDatePicker(mask) : maskType;
}

function timeMaskToLuxon(mask: string): string {
  return convertUnquotedTokens(mask, (token) => {
    if (token[0] === 'n') {
      return token.length > 1 ? 'mm' : 'm';
    }

    if (token[0] === 'S') {
      return token.length > 1 ? 'ss' : 's';
    }

    if (token[0] === 'z') {
      return 'ZZZZ';
    }

    return token;
  });
}

function convertUnquotedTokens(mask: string, convertToken: (token: string) => string): string {
  let convertedMask = '';
  let quoted = false;

  for (let index = 0; index < mask.length;) {
    const character = mask[index];

    if (character === "'") {
      if (mask[index + 1] === "'") {
        convertedMask += "''";
        index += 2;
        continue;
      }

      quoted = !quoted;
      convertedMask += character;
      index++;
      continue;
    }

    if (!quoted && /[A-Za-z]/.test(character)) {
      let token = character;
      index++;
      while (index < mask.length && mask[index] === character) {
        token += mask[index];
        index++;
      }

      convertedMask += convertToken(token);
      continue;
    }

    convertedMask += character;
    index++;
  }

  return convertedMask;
}
